import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { PostingApp, PostingSettings } from './model';

interface SettingsTabProps {
  app: PostingApp;
  settings: PostingSettings;
  canEdit: boolean;
  savingSettings: boolean;
  onUpdateSettings: (patch: Record<string, unknown>) => void;
  onMaterialize: () => void;
  materializing: boolean;
}

/** TDHP groups that hold monetary items — mirrors MONETARY_ACCOUNT_GROUPS on the server. */
const MONETARY_GROUPS = ['10', '11', '12', '13', '22', '23', '30', '32', '33', '34', '40', '42', '43', '44'];

export function parsePrefixes(raw: string): string[] {
  return [...new Set(raw.split(/[,\s;]+/).map((p) => p.trim()).filter(Boolean))].sort();
}

export function invalidPrefixes(prefixes: string[]): string[] {
  return prefixes.filter((p) => !/^\d{3}/.test(p) || !MONETARY_GROUPS.includes(p.slice(0, 2)));
}

/**
 * Policies only. Nothing here can switch off part of a flow: the engine
 * guarantees single recognition, balanced entries and class-checked accounts
 * whatever is chosen.
 */
export function SettingsTab({ app, settings, canEdit, savingSettings, onUpdateSettings, onMaterialize, materializing }: SettingsTabProps) {
  const { t } = useTranslation('postingRules');
  const policy = 'revenueRecognition' in settings ? settings.revenueRecognition : null;
  const storedPrefixes = settings.fxRevaluationAccounts ?? [];
  const [prefixDraft, setPrefixDraft] = useState(storedPrefixes.join(', '));
  useEffect(() => setPrefixDraft(storedPrefixes.join(', ')), [storedPrefixes.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const parsedPrefixes = parsePrefixes(prefixDraft);
  const badPrefixes = invalidPrefixes(parsedPrefixes);
  const prefixesDirty = parsedPrefixes.join(',') !== [...storedPrefixes].sort().join(',');

  return (
    <div className="space-y-6">
      {/* Precedence explainer */}
      <div className="border border-border bg-card p-4">
        <h3 className="font-semibold">{t('settings.precedenceTitle')}</h3>
        <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
          <li>{t('settings.precedence1')}</li>
          <li>{t('settings.precedence2')}</li>
          <li>{t('settings.precedence3')}</li>
          <li>{t('settings.precedence4')}</li>
        </ol>
      </div>

      {/* Invariants the engine enforces */}
      <div className="border border-border bg-card p-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
          <h3 className="font-semibold">{t('settings.guaranteesTitle')}</h3>
        </div>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          <li>{t('settings.guarantee1')}</li>
          <li>{t(app === 'TRAVEL' ? 'settings.guarantee2Travel' : 'settings.guarantee2Trading')}</li>
          <li>{t('settings.guarantee3')}</li>
          <li>{t('settings.guarantee4')}</li>
          <li>{t('settings.guarantee5')}</li>
        </ul>
      </div>

      {/* Revenue recognition (travel) */}
      {policy && (
        <div className="border border-border bg-card p-4">
          <h3 className="font-semibold">{t('settings.revenueTitle')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">{t('settings.revenueIntro')}</p>
          <div className="mt-3 grid gap-2 md:grid-cols-2">
            {(['IMMEDIATE', 'DEFERRED'] as const).map((value) => (
              <label
                key={value}
                className={cn(
                  'flex cursor-pointer items-start gap-3 border p-3 text-sm',
                  policy === value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/40',
                  !canEdit && 'cursor-not-allowed opacity-70'
                )}
              >
                <input
                  type="radio"
                  name="revenueRecognition"
                  className="mt-1"
                  checked={policy === value}
                  disabled={!canEdit || savingSettings}
                  onChange={() => onUpdateSettings({ revenueRecognition: value })}
                />
                <span>
                  <span className="font-medium">{t(`settings.revenue.${value}.label`)}</span>
                  <span className="mt-0.5 block text-muted-foreground">{t(`settings.revenue.${value}.description`)}</span>
                </span>
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t('settings.revenueNote')}</p>
        </div>
      )}

      {/* FX revaluation scope */}
      <div className="border border-border bg-card p-4">
        <h3 className="font-semibold">{t('settings.fxTitle')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('settings.fxIntro')}</p>
        <form
          className="mt-3 flex flex-wrap items-start gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (badPrefixes.length === 0 && parsedPrefixes.length > 0) onUpdateSettings({ fxRevaluationAccounts: parsedPrefixes });
          }}
        >
          <div className="min-w-[280px] flex-1">
            <Input
              value={prefixDraft}
              onChange={(e) => setPrefixDraft(e.target.value)}
              disabled={!canEdit || savingSettings}
              placeholder="100, 102, 120, 320"
              className="h-8 font-mono text-xs"
            />
            {badPrefixes.length > 0 && (
              <p className="mt-1 text-[11px] text-red-700 dark:text-red-300">{t('settings.fxInvalid', { list: badPrefixes.join(', '), groups: MONETARY_GROUPS.join(', ') })}</p>
            )}
          </div>
          <Button type="submit" className="h-8" variant="outline" disabled={!canEdit || savingSettings || !prefixesDirty || badPrefixes.length > 0 || parsedPrefixes.length === 0}>
            {savingSettings ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('settings.fxSave')}
          </Button>
        </form>
        <p className="mt-2 text-xs text-muted-foreground">{t('settings.fxHint', { groups: MONETARY_GROUPS.join(', ') })}</p>
      </div>

      {/* Materialize defaults */}
      <div className="border border-border bg-card p-4">
        <h3 className="font-semibold">{t('settings.materializeTitle')}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t('settings.materializeIntro')}</p>
        <Button className="mt-3 h-8" variant="outline" disabled={!canEdit || materializing} onClick={onMaterialize}>
          {materializing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
          {t('settings.materializeAction')}
        </Button>
      </div>
    </div>
  );
}
